import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ModuleName } from 'src/app/shared/enums/ModuleName';
import { AppInjector } from 'src/app/app.module';
import { AnalysisFilterKeys } from '../domain/analysis-filter-keys';
import { GeneralService } from './general.service';
import { ExtractPipe } from 'src/app/shared/pipes/extract.pipe';
import { EssentialObject, EssentialObjectService } from './essential-object.service';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import countryList from "../../../assets/Translations/Countries.json"

export const KEY_RESOLVER_TOKEN = new InjectionToken<IKeyResolver[]>("KEY_RESOLVER_TOKEN");

@Injectable({
  providedIn: 'root'
})
export class KeyResolverService {

  keyResolver: IKeyResolver;

  /**
   * Default Resolvers
   */
  resolvers: IKeyResolver[] = [];

  registerResolvers(resolvers: IKeyResolver[]) {
    for (let resolver of resolvers) {
      if (!this.resolvers.find(x => x.name == resolver.name))
        this.resolvers.push(resolver);
    }
  }

  setKeyResolver(resolver: IKeyResolver) {
    this.keyResolver = resolver;
  }

  resolve(key: string, language: string, options?: any) {
    return this.keyResolver.resolve(key, language, options);
  }

  getResolver(name: string) {
    return this.resolvers.find(x => x.name == name || x.name.toLowerCase() == name.toLowerCase());
  }

}

export interface IKeyResolver {
  name: string;
  resolve(key: string, language: string, options?: any): string
}

@Injectable({
  providedIn: null,
})
export class NullResolver implements IKeyResolver {
  name: string = "null";
  resolve(key: string, language: string): string {
    return null;
  }

}

@Injectable({
  providedIn: null,
})
export class GenderResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.Gender;
  readonly NA: string = 'txtNA';

  /**
   *
   */
  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string): string {
    let translation = this.generalService.genders_new.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name, language) || key;
  }
}

@Injectable({
  providedIn: null,
})
export class AgeGroupResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.AgeGroups;
  readonly NA: string = 'txtNA';

  /**
   *
   */
  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string): string {
    let translation = this.generalService.ageGroups_new.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name || key, language) || key;
  }
}

@Injectable({
  providedIn: null,
})
export class TenureGroupResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.TenureGroups;
  readonly NA: string = 'txtNA';

  /**
   *
   */
  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string): string {
    let translation = this.generalService.tenureGroups_new.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name, language) || key;
  }
}

@Injectable({
  providedIn: null,
})
export class GenerationGroupResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.Generation;
  readonly NA: string = 'txtNA';

  /**
   *
   */
  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string): string {
    let translation = this.generalService.generations_new.find(x => x.key == key);
    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name, language) || key;
  }
}

@Injectable({
  providedIn: null,
})
export class NationalityResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.Nationality;
  readonly NA: string = 'txtNA';

  /**
   *
   */
  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string): string {
    let translation = this.generalService.countries_new.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name, language) || key;
  }
}

@Injectable({
  providedIn: null,
})
export class JobGradeResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.JobGrade;
  readonly NA: string = 'txtNA';
  private translations = [];

  /**
   *
   */
  constructor(private essentialObjectService: EssentialObjectService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {
    this.essentialObjectService.essentialObject
      .pipe(
        filter(obj => obj?.selectedEntity?.key != null),
        distinctUntilChanged((a, b) => a == b, obj => obj.selectedEntity.key)
      )
      .subscribe(obj => {
        console.log("in resolver", obj);
        if (obj?.selectedEntity?.key) {
          this.translations = obj?.selectedEntity?.JobGrades;
        }

      })
  }

  resolve(key: string, language: string): string {

    let translation = this.translations?.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return translation?.name ? this.extractPipe.transform(translation?.name || key, language) : key;
  }
}

@Injectable({
  providedIn: null,
})
export class BandLevelResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.BandLevel;
  readonly NA: string = 'txtNA';
  private translations = [];

  /**
   *
   */
  constructor(private essentialObjectService: EssentialObjectService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {
    this.essentialObjectService.essentialObject
      .pipe(
        filter(obj => obj?.selectedEntity?.key != null),
        distinctUntilChanged((a, b) => a == b, obj => obj.selectedEntity.key)
      )
      .subscribe((obj: EssentialObject) => {
        console.log("in resolver", obj);
        if (obj?.selectedEntity?.key) {
          this.translations = obj?.selectedEntity?.BandLevels;
        }

      })
  }

  resolve(key: string, language: string): string {

    let translation = this.translations?.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return translation?.name ? this.extractPipe.transform(translation?.name || key, language) : key;
  }
}

@Injectable({
  providedIn: null,
})
export class PerformanceRatingResolver implements IKeyResolver {

  name: string = AnalysisFilterKeys.PerformanceRating;
  readonly NA: string = 'txtNA';
  private translations = [];

  /**
   *
   */
  constructor(private essentialObjectService: EssentialObjectService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {
    this.essentialObjectService.essentialObject
      .pipe(
        filter(obj => obj?.selectedEntity?.key != null),
        distinctUntilChanged((a, b) => a == b, obj => obj.selectedEntity.key)
      )
      .subscribe((obj: EssentialObject) => {
        if (obj?.selectedEntity?.key) {
          this.translations = obj?.selectedEntity?.PerformanceRates;
        }
      })
  }

  resolve(key: string, language: string): string {

    let translation = this.translations?.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return translation?.name ? this.extractPipe.transform(translation?.name || key, language) : translation?.value ? translation.value : key;
  }
}


@Injectable({
  providedIn: null,
})
export class ExtraCriteriaResolver implements IKeyResolver {
  name: string = "EC";
  readonly NA: string = 'txtNA';

  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {

  }

  resolve(key: string, language: string, options?: any): string {
    var extraCriteria = this.generalService.extraCriterias.find(x => x.key == options);

    if (!key && !extraCriteria) {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    if (!extraCriteria) {
      return "";
    }

    var translation = extraCriteria.options.find(x => x.key == key);

    return this.extractPipe.transform(translation?.name || key, language) || key;
  }

}


@Injectable({
  providedIn: null,
})
export class EntityGroupResolver implements IKeyResolver {

  name: string = 'Entity';
  readonly NA: string = 'txtNA';
  private translations = [];

  constructor(private essentialObjectService: EssentialObjectService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {

    this.essentialObjectService.essentialObject
      .pipe(
        filter(obj => obj?.selectedEntity?.key != null),
        distinctUntilChanged((a, b) => a == b, obj => obj.selectedEntity.key)
      )
      .subscribe(obj => {

        if (obj?.selectedEntity?.key) {
          this.translations = obj?.allEntities;
        }


      })

  }

  resolve(key: string, language: string): string {
    let entityName = "";
    let parentName = "";

    let translation = this.translations.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    let parent = this.translations.find(x => x.key == translation?.parentKey);

    entityName = translation ? this.extractPipe.transform(translation.name, language) : key;
    parentName = parent ? this.extractPipe.transform(parent.name, language) : "";

    return parentName == "" ? `${entityName}` : `${parentName} - ${entityName}`;
  }

}

@Injectable({
  providedIn: null,
})
export class FixedGroupingOptionsResolver implements IKeyResolver {

  constructor(private generalService: GeneralService, private extractPipe: ExtractPipe, private translatePipe: TranslatePipe) {

  }

  name: string = "FixedGroupingOptions";
  readonly NA: string = 'txtNA';

  resolve(key: string, language: string, options?: any): string {
    var translation = this.generalService.fixedGroupingOptions.find(x => x.key == key);

    if ((!key && !translation) || key == 'null') {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }

    return this.extractPipe.transform(translation?.name || key, language);
  }

}

@Injectable({
  providedIn: null,
})
export class OnboardingExperienceScaleResolver implements IKeyResolver {
  name: string = "ExpScale";
  readonly NA: string = 'txtNA';

  constructor(private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string, options?: any): string {
    if (!key) {
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);
    }
    return this.translatePipe.transform(key, language, options);
  }

}

@Injectable({
  providedIn: null,
})
export class SurveyStatusResolver implements IKeyResolver {
  name: string = "SurveyStatus";
  readonly NA: string = 'txtNA';

  constructor(private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string, options?: any): string {

    if (key == null)
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);

    const txtKey = `txt_${key.toUpperCase()}`

    return this.translatePipe.transform(txtKey, language, options);
  }

}


@Injectable({
  providedIn: null,
})
export class JobRoleResolver implements IKeyResolver {
  name: string = AnalysisFilterKeys.JobRole;
  readonly NA: string = 'txtNA';

  constructor(private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string, options?: any): string {

    if (key == null)
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);

    const txtKey = `txt_${key.toUpperCase()}`

    return this.translatePipe.transform(txtKey, language, options);
  }

}

@Injectable({
  providedIn: null,
})
export class IndustryResolver implements IKeyResolver {
  name: string = AnalysisFilterKeys.Industry;
  readonly NA: string = 'txtNA';

  constructor(private translatePipe: TranslatePipe, private generalService: GeneralService) { }

  resolve(key: string, language: string, options?: any): string {

    if (key == null)
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);

    const option = this.generalService.industries.find(x => x.key == key && x.language == language);

    return option ? option.name : key;
  }

}

@Injectable({
  providedIn: null,
})
export class CountryResolver implements IKeyResolver {
  name: string = AnalysisFilterKeys.Country;
  readonly NA: string = 'txtNA';

  constructor(private extractPipe: ExtractPipe, private generalService: GeneralService, private translatePipe: TranslatePipe) { }

  resolve(key: string, language: string, options?: any): string {

    if (key == null)
      return this.translatePipe.transform(this.NA, language, ModuleName.Shared);

    const option = countryList.find(x => x.key == key);

    return option ? this.extractPipe.transform(option.name, language) : key;
  }

}


export { AnalysisFilterKeys };
/**
 *
    Problem Statement: Resolve the keys to their equivalent localized text based
    on the current selected system language

    key types that need to be resolved to their equivalent localized text
    1. Gender
    2. Performance Ratings
    3. Age Groups
    4. Tenure Groups
    5. Generation Groups
    6. Entity Hierarchy
    7. Nationality
    8. ..


 */
